import { multischeduleEnabled } from '@utils/consts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { InternalScheduleDialogProps } from './types';
import { useTranslation } from 'react-i18next';
import { useDate } from '@hooks/date';
import { useEffect, useRef, useState } from 'react';
import { useGetUsersSchedules } from '@services/schedules/internal';
import { useGetUsersSchedule } from '@services/schedules/internal/get';
import { Form, FormField } from '@components/ui/form';
import { useInternalScheduleForm } from '@forms/schedules/internal';
import { useToast } from '@hooks/useToast';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { Input } from '@components/ui/form/input';
import { Checkbox } from '@components/ui/checkbox';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { formatDate } from '@utils/dateHelpers';
import { useSchedulerContext } from '@contexts/scheduler';
import { initialState } from '@contexts/scheduler/constants';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { initialState as sidebarInitialState } from '@contexts/scheduler/sidebar/constants';
import { SchedulerSidebarContextData } from '@contexts/scheduler/sidebar/types';
import Scheduler from '@components/scheduler';
import { useRescheduler } from '@contexts/rescheduler';
import { handleRelatedSchedulesResponse } from '@contexts/rescheduler/helpers';
import { ReschedulerContextData } from '@contexts/rescheduler/types';
import { RemoveScheduleDialog } from '../remove';
import { SchedulerDate } from '@components/scheduler/date';
import { useQueryParams } from '@hooks/queryParams';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useSchedulerMaxHeight } from '@hooks/scheduler/maxHeight';
import { SpecialtyOverrideDialog } from './action/specialty';
import { OverlapScheduleDialog } from './action/overlap';
import { InternalScheduleActions } from './actions';
import { SchedulerSidebar } from '@components/scheduler/sidebar';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Conflict } from './publish/types';
import { formatTime, shortDate, shortDayDate } from '@utils/i18n';
import {
  getDisplayOptionsSettings,
  getWorkloadDisplayOptionsSettingsEndpoint,
} from '@components/workload/drawer/displayOptions/helpers';
import { useAppContext } from '@contexts/app';
import { cn } from '@utils/cn';
import { getMultiDayPosition, isExistingMultiDay } from '@contexts/scheduler/sidebar/helpers';
import { getJobSubtitle, getSkipWeekendDefaults, scrollActiveScheduleIntoView } from './helpers';
import { InternalScheduleMultiDayInputs } from './inputs/multiDay';
import { InternalScheduleExtendDaysInputs } from './inputs/extendDays';
import { InternalScheduleHeaderPending } from './inputs/pending';
import { getMultiDayDates, getSeriesDays } from '@forms/schedules/internal/helpers';
import { SeriesCalendarLegend } from './seriesCalendar';
import { seriesDayCalendarStyle } from './seriesCalendar/constants';
import { getSeriesCalendarModifiers, hasSeriesCalendarDays } from './seriesCalendar/helpers';

export default function InternalScheduleDialog({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  activeId,
  readOnly,
  initialParams = {},
}: InternalScheduleDialogProps) {
  const { t } = useTranslation('app');
  const { displayOptions, tenant } = useAppContext();
  const { toast } = useToast();
  const { active, ...scheduler } = useSchedulerContext();
  const {
    setState: setSidebarState,
    relatedMultischedules,
    currentSchedule,
    plannedDays,
    extensionDates,
  } = useSchedulerSidebar() as SchedulerSidebarContextData;
  const rescheduler = useRescheduler();
  const popoverRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const [overlap, setOverlap] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [specialtyOverride, setSpecialtyOverride] = useState(false);
  const [remove, setRemove] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedForemen, setSelectedForemen] = useState<string[]>(initialParams.users || []);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const maxHeight = useSchedulerMaxHeight(popoverRef);
  const {
    params: { allUsers, type: calendarType },
    setParam,
  } = useQueryParams();

  const { specialties, fieldUsers } = useDropdownsContext();

  const skipSpecialtyConfirmation = getDisplayOptionsSettings(
    displayOptions || [],
    getWorkloadDisplayOptionsSettingsEndpoint(calendarType),
  )?.schedulingSettingsHideMissingSpecialties;

  const { date, rawDate, setRawDate } = useDate({
    initialDate,
    disableLoadFromParams: true,
    disableParamsUpdate: true,
  });

  const { data, isFetching: schedulesFetching } = useGetUsersSchedules({
    date: formatDate(date),
    specialties: selectedSpecialties,
    users: selectedForemen,
    active: open && isInitialized,
    crewTypes: ['field_user'],
    divisionIds: [schedule?.task?.job?.division?.id || ''],
    scheduleId: schedule?.id,
  });

  const { error: currentScheduleError } = useGetUsersSchedule(
    open && schedule?.id !== 'new' ? schedule?.id : undefined,
  );

  const removeSchedule = useRemoveInternalSchedule({
    id: active.schedule.id,
    taskId: active.schedule.task?.id || '',
    jobId,
    callback: ({ success }) => {
      if (!success) return;
      setOpen(false);
    },
  });

  const { form, onSubmit, locked } = useInternalScheduleForm({
    successHandler: (resp) => {
      const draftSent = schedule?.status === 'draft' && resp?.data?.status !== schedule?.status;
      form.reset();
      setParam('allUsers', '');
      setOpen(false);
      setOverlap(false);
      setSpecialtyOverride(false);
      toast({
        title: t(
          `messages.success.schedule.${draftSent ? 'saveAndSend' : schedule?.status ? 'update' : 'create'}`,
        ),
      });
      if (!resp || !rescheduler) return;
      handleRelatedSchedulesResponse(rescheduler as ReschedulerContextData, resp);
    },
    onOverlap: (conflicts) => {
      setOverlap(true);
      setConflicts(conflicts);
    },
    onSpecialtyOverride: () => setSpecialtyOverride(true),
  });

  const { watch, setValue, resetField } = form;

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
      return;
    }
    setSelectedSpecialties(type ? [type.value] : []);
    setSelectedForemen(initialParams.users || []);
    if (initialDate) setRawDate(initialDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  useEffect(() => {
    if (!open || isInitialized) return;

    const specialtiesReady =
      JSON.stringify(selectedSpecialties) === JSON.stringify(type ? [type.value] : []);
    const foremenReady =
      JSON.stringify(selectedForemen) === JSON.stringify(initialParams.users || []);
    const dateReady = !initialDate || formatDate(date) === formatDate(initialDate);

    if (specialtiesReady && foremenReady && dateReady) setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isInitialized, selectedSpecialties, selectedForemen, date]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!open || !type?.value) return;
    const arr =
      (active?.schedule?.foreman?.specialties?.length
        ? active?.schedule?.foreman?.specialties?.map((el) => el.value)
        : initialParams.specialties) || [];
    setValue('forceSpecialty', arr.length ? arr.includes(type?.value) : true);
  }, [
    open,
    setValue,
    type?.value,
    active?.schedule?.foreman?.specialties,
    initialParams.specialties,
    skipSpecialtyConfirmation,
  ]);

  useEffect(() => {
    if (!open) return;

    setSidebarState((prev) => ({ ...prev, populateDefaultCrewAndEquipment: true }));

    return () => {
      setSidebarState(sidebarInitialState);
    };
  }, [open, setSidebarState]);

  useEffect(() => {
    if (locked || overlap) return;
    if (!open) {
      resetField('note');
      resetField('status');
      resetField('days');
      resetField('addDays');
      resetField('skipSaturday');
      resetField('skipSunday');
      scheduler.setState(initialState);
      setSidebarState(sidebarInitialState);

      return;
    }

    const foremanId = active.schedule.foreman?.id || schedule?.foreman?.id || '';
    setValue('foremanId', foremanId);

    if (!watch('note')) setValue('note', schedule?.task?.note?.content || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialDate,
    open,
    schedule,
    type,
    locked,
    setValue,
    active.schedule.foreman?.id,
    active.schedule.id,
  ]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!selectedForemen.length) return;
    const currentForemanId = active.schedule.foreman?.id || schedule?.foreman?.id;
    if (!currentForemanId) return;

    setValue('forceSpecialty', selectedForemen.includes(currentForemanId));
  }, [
    selectedForemen,
    skipSpecialtyConfirmation,
    setValue,
    schedule?.foreman?.id,
    active.schedule.foreman?.id,
  ]);

  useEffect(() => {
    if (schedule?.id === 'new' || !schedule?.id || ['approved', 'draft'].includes(schedule.status))
      setValue('confirmed', true);
  }, [schedule?.id, schedule?.status, schedule?.task?.id, setValue]);

  useEffect(() => {
    if (!open || !active.fresh) return;
    const frame = requestAnimationFrame(() => {
      if (gridScrollRef.current) scrollActiveScheduleIntoView(gridScrollRef.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, active.fresh, active.schedule.id, active.schedule.foreman?.id]);

  const jobSubtitle = getJobSubtitle(schedule);
  const isNewSchedule = !schedule?.id || schedule.id === 'new';
  const canScheduleMultiDay =
    multischeduleEnabled &&
    !readOnly &&
    !schedule?.task?.multischedule &&
    (isNewSchedule || Boolean(currentSchedule?.id)) &&
    !isExistingMultiDay(relatedMultischedules);
  const canExtendMultiDay =
    multischeduleEnabled &&
    !readOnly &&
    !isNewSchedule &&
    Boolean(currentSchedule?.id) &&
    (Boolean(schedule?.task?.multischedule) || isExistingMultiDay(relatedMultischedules));
  const multiDayPosition = getMultiDayPosition({
    relatedMultischedules,
    currentSchedule,
    plannedDays: canScheduleMultiDay ? plannedDays : 1,
  });
  const weekendDefaults = getSkipWeekendDefaults(displayOptions || [], calendarType);
  const [plannedCount, skipSaturday, skipSunday] = watch(['days', 'skipSaturday', 'skipSunday']);
  const isSeries =
    Boolean(currentSchedule?.id) &&
    (Boolean(schedule?.task?.multischedule) || isExistingMultiDay(relatedMultischedules));
  const addedDates = canScheduleMultiDay
    ? getMultiDayDates(rawDate, plannedCount, { skipSaturday, skipSunday }).slice(1)
    : canExtendMultiDay
      ? extensionDates
      : [];
  const seriesDays = isSeries
    ? getSeriesDays({
        relatedMultischedules,
        currentScheduleId: currentSchedule?.id,
        startsAt: active.schedule.startsAt,
        zone: tenant.zone,
      })
    : [];
  const calendarModifiers = getSeriesCalendarModifiers({
    relatedMultischedules: isSeries ? relatedMultischedules : undefined,
    currentScheduleId: currentSchedule?.id,
    addedDates,
    zone: tenant.zone,
  });
  const seriesRange =
    seriesDays.length > 1
      ? [seriesDays[0], seriesDays.at(-1) as Date]
      : addedDates.length
        ? [rawDate, addedDates.at(-1) as Date]
        : null;
  const showSeriesCalendar = hasSeriesCalendarDays(calendarModifiers);
  const dateControl = (
    <SchedulerDate
      date={rawDate}
      setDate={setRawDate}
      loading={Boolean(schedulesFetching)}
      calendar={{
        open: calendarOpen,
        setOpen: setCalendarOpen,
        ...(showSeriesCalendar && {
          modifiers: calendarModifiers,
          modifiersClassNames: seriesDayCalendarStyle,
          legend: <SeriesCalendarLegend modifiers={calendarModifiers} />,
        }),
      }}
    />
  );
  const seriesPending =
    !readOnly && !isNewSchedule && !currentSchedule?.id && !currentScheduleError;
  const dateCaption = multiDayPosition && (
    <button
      type="button"
      className="text-xs text-neutral-500 underline-offset-2 hover:text-neutral-800 hover:underline"
      onClick={() => setCalendarOpen(true)}>
      {[
        t('schedule.multiDay.dayOf', multiDayPosition),
        seriesRange && [...new Set(seriesRange.map(shortDate))].join(' – '),
      ]
        .filter(Boolean)
        .join(' · ')}
    </button>
  );
  const schedulerStyle = maxHeight > 0 ? { height: maxHeight } : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          ref={popoverRef}
          className="w-[calc(100%-3rem)] gap-0 overflow-hidden p-0"
          onEscapeKeyDown={(e) => {
            if (document.activeElement?.hasAttribute('data-revert-on-escape')) e.preventDefault();
          }}>
          <DialogHeader className="scheduler-navigation sticky top-0 z-10 grid grid-cols-[1fr_auto_1fr] items-start gap-6 space-y-0 bg-white p-6 shadow-md">
            <div className="flex flex-col gap-0.5">
              <DialogTitle className="h-fit">
                {t('schedule.team', { team: type?.name })}
              </DialogTitle>
              {jobSubtitle && <DialogDescription>{jobSubtitle}</DialogDescription>}
            </div>
            {seriesPending ? (
              <InternalScheduleHeaderPending dateControl={dateControl} />
            ) : canScheduleMultiDay ? (
              <Form {...form}>
                <InternalScheduleMultiDayInputs
                  startDate={rawDate}
                  disabled={locked}
                  weekendDefaults={weekendDefaults}
                  popoverRef={popoverRef}
                  dateControl={dateControl}
                  dateCaption={dateCaption}
                />
              </Form>
            ) : canExtendMultiDay ? (
              <Form {...form}>
                <InternalScheduleExtendDaysInputs
                  disabled={locked}
                  weekendDefaults={weekendDefaults}
                  dateControl={dateControl}
                  dateCaption={dateCaption}
                />
              </Form>
            ) : (
              <div className="flex flex-col items-center">
                {dateControl}
                {dateCaption}
              </div>
            )}
            <div />
          </DialogHeader>
          <div
            className="mt-3 flex min-h-0 min-w-0 items-stretch overflow-hidden"
            style={schedulerStyle}>
            <div className="flex min-h-0 flex-1 flex-col pl-6">
              <div className="flex items-center justify-between gap-4 pb-3 pr-6 pt-1.5">
                {!readOnly ? (
                  <Form {...form}>
                    <div className="flex flex-wrap items-center gap-4">
                      <MultiSelect
                        placeholder={t('filters.teams.placeholder')}
                        className="w-fit whitespace-nowrap"
                        options={specialties.options.map((el) => ({
                          value: el.value,
                          label: el.name,
                        }))}
                        value={selectedSpecialties}
                        limit={1}
                        onChange={setSelectedSpecialties}
                        disabled={specialties.disabled}
                        side="bottom"
                        popoverRef={popoverRef}
                      />
                      <MultiSelect
                        placeholder={t('filters.foremen.placeholder')}
                        className="w-fit whitespace-nowrap"
                        options={fieldUsers.options
                          .filter((el) =>
                            selectedForemen.length
                              ? !data?.records.some(
                                  (record) =>
                                    record.id === el.id && !selectedForemen.includes(el.id),
                                )
                              : true,
                          )
                          .map((el) => ({
                            value: el.id,
                            label: el.fullName || '',
                          }))}
                        value={selectedForemen}
                        limit={1}
                        onChange={setSelectedForemen}
                        disabled={fieldUsers.disabled}
                        side="bottom"
                        popoverRef={popoverRef}
                      />
                      <Checkbox
                        id="allDivisions"
                        checked={allUsers === 'true'}
                        onCheckedChange={() =>
                          setParam('allUsers', allUsers === 'true' ? '' : 'true')
                        }>
                        {t('schedules.allDivisions')}
                      </Checkbox>
                    </div>
                  </Form>
                ) : (
                  <div />
                )}
                <Button
                  variant="ghost"
                  className="h-fit shrink-0 gap-1.5 p-1 text-xs text-neutral-700 hover:bg-transparent hover:text-neutral-900"
                  onClick={() => setSidebarExpanded(!sidebarExpanded)}>
                  <span
                    className={cn(
                      'flex shrink-0 items-center text-xs',
                      !sidebarExpanded && '-scale-x-100',
                    )}>
                    <SvgConsumer id="arrow" />
                    <SvgConsumer id="arrow" className="-ml-1.5" />
                  </span>
                  {sidebarExpanded ? t('actions.hide') : t('actions.show')}
                </Button>
              </div>
              <div
                ref={gridScrollRef}
                className="scheduler-container scrollbar-thin min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-6">
                <Scheduler
                  modal
                  date={rawDate}
                  foremen={data?.records}
                  withoutTeam={data?.withoutTeam}
                  outOfScopeRecords={data?.outOfScopeRecords}
                  schedule={schedule}
                  activeId={activeId}
                  restrictTo="schedulerGrid"
                />
              </div>
            </div>
            {sidebarExpanded && (
              <div className="flex min-h-0 min-w-[300px] max-w-[35%] shrink grow-0 basis-[420px] flex-col">
                <SchedulerSidebar />
              </div>
            )}
          </div>
          <div ref={popoverRef} className="overflow-visible" />
          <Form {...form}>
            <form
              className="dialog-footer border-t p-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="forceOverlapping"
                render={() => (
                  <FormItemWrapper>
                    <input type="hidden" />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="foremanId"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItemWrapper label={t('forms.note.label')}>
                    <Input disabled={readOnly} {...field} />
                  </FormItemWrapper>
                )}
              />
              <div className="flex flex-col gap-2">
                <div className="mt-6 flex items-start justify-end gap-2">
                  {!readOnly && (
                    <div className="flex h-10 flex-1 items-center justify-between gap-4">
                      <div>
                        {schedule?.id && schedule.id !== 'new' && (
                          <Button
                            variant="dangerSecondary"
                            disabled={removeSchedule.isLoading}
                            onClick={() => setRemove(true)}>
                            {t('actions.removeSchedule')}
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-grid items-center gap-2">
                        <FormField
                          control={form.control}
                          name="confirmed"
                          render={({ field: { value, onChange } }) => (
                            <FormItemWrapper
                              className={cn('w-fit', schedule?.status === 'draft' && 'mt-7')}>
                              <div className="flex gap-2">
                                <Checkbox
                                  id="confirmed"
                                  checked={Boolean(value)}
                                  onCheckedChange={onChange}>
                                  {t('schedules.markAsConfirmed')}
                                </Checkbox>
                              </div>
                            </FormItemWrapper>
                          )}
                        />
                        <InternalScheduleActions
                          onCancel={() => {
                            form.reset();
                            setOpen(false);
                          }}
                          onSendSchedule={() => {
                            setValue('actionType', 'saveAndSend');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onRescheduleAsDraft={() => {
                            setValue('status', 'draft');
                            setValue('actionType', '');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onSaveAsDraft={() => setValue('actionType', '')}
                          locked={locked || !active.schedule.id}
                          scheduleId={schedule?.id}
                          foremanId={active.schedule.foreman?.id}
                          readOnly={readOnly}
                          isDraft={schedule?.status === 'draft'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
            {overlap && (
              <OverlapScheduleDialog
                open={overlap}
                setOpen={setOverlap}
                disabled={locked || !active.schedule.id}
                onCancel={() => setValue('forceOverlapping', false)}
                onConfirm={() => {
                  setValue('forceOverlapping', true);
                  void form.handleSubmit(onSubmit)();
                }}
                conflicts={conflicts || []}
                date={
                  active.schedule?.startsAt ? shortDayDate(new Date(active.schedule.startsAt)) : ''
                }
                name={active.schedule?.task?.name}
                summaryDetail={
                  active.schedule?.startsAt
                    ? `${[
                        formatTime(new Date(active.schedule.startsAt)),
                        formatTime(new Date(active.schedule?.endsAt || '')),
                      ].join(' - ')}, ${shortDayDate(new Date(active.schedule.startsAt))}`
                    : ''
                }
              />
            )}
            <SpecialtyOverrideDialog
              open={specialtyOverride}
              setOpen={setSpecialtyOverride}
              disabled={locked || !active.schedule.id}
              onCancel={() => setValue('forceSpecialty', false)}
              onConfirm={() => {
                setValue('forceSpecialty', true);
                void form.handleSubmit(onSubmit)();
              }}
            />
          </Form>
        </DialogContent>
      </Dialog>
      <RemoveScheduleDialog
        open={remove}
        setOpen={setRemove}
        onConfirm={removeSchedule.mutate}
        disabled={removeSchedule.isLoading}
      />
    </>
  );
}
